import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { useContext } from 'react';
import { UserContext } from '../UserContext';
import { useTranslation } from '../LanguageContext';
import { useWebSocket } from '../components/socket';
import api from '../api';
import StreamPlayer from '../components/StreamPlayer';


const subdomain = "customer-uqadfqxwoqnv17yr.cloudflarestream.com"
const imageDelivery = "https://imagedelivery.net/UWWbDbFZQHdAopnGn06zDQ"

const isoDate = (date) => {
    const dateObj = new Date(date);
    return `${dateObj.getDate()}-${dateObj.getMonth() + 1}-${dateObj.getFullYear()} ${dateObj.getHours()}:${dateObj.getMinutes()}`;
}

const VideoMessage = ({ message, isMine }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isReady, setIsReady] = useState(false);
    // const videoId = message.text.split('/')[1]; // Extract UID from "subdomain/uid"

    useEffect(() => {
        let attempts = 0;
        const maxAttempts = 30; // 1 minute of checking (2s interval)

        const checkVideoStatus = async () => {
            if (isReady) return;
            try {
                // Try to fetch the video manifest. If it exists, video processing is complete.
                const res = await fetch(`https://${message.text}/manifest/video.m3u8`, { method: 'HEAD' });
                if (res.ok) {
                    setIsReady(true);
                    setIsLoading(false);
                } else {
                    attempts++;
                    if (attempts < maxAttempts) {
                        setTimeout(checkVideoStatus, 2000);
                    } else {
                        // Give up after timeout and just show the player
                        setIsLoading(false);
                    }
                }
            } catch (e) {
                attempts++;
                if (attempts < maxAttempts) setTimeout(checkVideoStatus, 2000);
            }
        };

        checkVideoStatus();
        return () => { attempts = maxAttempts; }; // Cleanup
    }, [message.text, isReady]);

    return (
        <div className="w-64 sm:w-80">
            <div className="relative bg-slate-900" style={{ paddingTop: '56.25%' }}>
                {isLoading ? (
                    <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-900">
                        <div className="flex flex-col items-center gap-2">
                            <div className="spinner-sm border-white/20 border-t-white"></div>
                            <span className="text-[10px] text-white/70">Processing video...</span>
                        </div>
                    </div>
                ) : (
                    <StreamPlayer
                        videoUrl={message.text}
                        autoPlay={false}
                        controls={true}
                        className="absolute inset-0 w-full h-full transition-opacity duration-300 opacity-100"
                        poster={`https://${message.text}/thumbnails/thumbnail.jpg?time=2s`}
                    />
                )}
            </div>
            <div className="px-3 py-1.5">
                <span className={`text-[10px] block text-right ${isMine ? 'text-white/60' : 'text-slate-400'}`}>{isoDate(message.date)}</span>
            </div>
        </div>
    );
};

function Chats() {
    const { t } = useTranslation();
    const [sendingMessage, setsendingMessage] = useState("");
    const { user, setUser } = useContext(UserContext);
    const shouldScrollRef = useRef(true);
    const { socket } = useWebSocket();
    const messageEndRef = useRef(null);
    const chatContainerRef = useRef(null);
    const scrollState = useRef({ isPrepending: false, height: 0, top: 0 });
    const active_id = user.active;
    const [activeUser, setActiveUser] = useState(user.dialogues.find((dialogue) => dialogue.id === active_id) || {});
    const [isChatboxVisible, setIsChatboxVisible] = useState(false);
    const [searchlist, setSearchlist] = useState(user.dialogues);
    const [search, setSearch] = useState("");
    const [activeTab, setActiveTab] = useState("chats");
    const [uploadUrl, setUploadUrl] = useState(null);
    const [videoUid, setVideoUid] = useState(null);
    const [videoFile, setVideoFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const videoInputRef = useRef(null);
    const [photoUploadUrl, setPhotoUploadUrl] = useState(null);
    const [photoImageId, setPhotoImageId] = useState(null);
    const [photoFile, setPhotoFile] = useState(null);
    const [photoUploading, setPhotoUploading] = useState(false);
    const photoInputRef = useRef(null);

    // Star sending state
    const [isStarModalOpen, setIsStarModalOpen] = useState(false);
    const [starAmount, setStarAmount] = useState("");
    const [starError, setStarError] = useState("");

    // Collapsible attachment menu state
    const [isAttachMenuOpen, setIsAttachMenuOpen] = useState(false);

    // Archived users state (stored in localStorage, no backend needed)
    const [archivedUsers, setArchivedUsers] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('archived_users') || '[]');
        } catch { return []; }
    });

    const isArchived = (userId) => archivedUsers.includes(userId);

    const archiveUser = (userId) => {
        const updated = [...archivedUsers, userId];
        setArchivedUsers(updated);
        localStorage.setItem('archived_users', JSON.stringify(updated));
    };

    const unarchiveUser = (userId) => {
        const updated = archivedUsers.filter(id => id !== userId);
        setArchivedUsers(updated);
        localStorage.setItem('archived_users', JSON.stringify(updated));
    };

    // Sort dialogues by most recent message (sent or received)
    const sortByLatestMessage = (dialogues) => {
        return [...dialogues].sort((a, b) => {
            const lastMsgA = [...user.messages].reverse().find(msg => msg.sender === a.id || msg.receiver === a.id);
            const lastMsgB = [...user.messages].reverse().find(msg => msg.sender === b.id || msg.receiver === b.id);
            const dateA = lastMsgA ? new Date(lastMsgA.date).getTime() : 0;
            const dateB = lastMsgB ? new Date(lastMsgB.date).getTime() : 0;
            return dateB - dateA;
        });
    };


    const handleSearch = (e) => {
        setSearch(e);
        if (e === "") {
            setSearchlist(sortByLatestMessage(user.dialogues));
        } else {
            setSearch(e);
        }
    };

    useEffect(() => {
        setSearchlist(
            sortByLatestMessage(user.dialogues.filter((dialogue) =>
                dialogue.username.toLowerCase().startsWith(search.toLowerCase())
            ))
        );
        // eslint-disable-next-line
    }, [search]);

    const showChatbox = () => setIsChatboxVisible(true);
    const hideChatbox = () => { setIsChatboxVisible(false); setActiveUser({}); };



    const scrollToBottom = () => {
        if (messageEndRef.current) {
            messageEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    function is_include() {
        try {
            if (user.messages.filter(msg => msg.sender === activeUser.id || msg.receiver === activeUser.id).length < 30) {
                return false;
            }
            let u = user.last.filter(msg => msg.sender === activeUser.id || msg.receiver === activeUser.id)[0].id;
            user.messages.filter(msg => msg.sender === activeUser.id || msg.receiver === activeUser.id).forEach(element => {
                if (element.id === u) { return true; }
                else { return false; }
            });
        } catch (error) {
            return true;
        }
    }

    function delete_notification() {
        setUser(prevUser => ({
            ...prevUser,
            notifications: [...prevUser.notifications.filter(element => element !== activeUser.id)]
        }));
        if (socket && (activeUser.id)) {
            sendNotification();
        }
    }

    useEffect(() => {
        if (shouldScrollRef.current && user.messages.length > 0 && (user.messages[user.messages.length - 1].sender === activeUser.id || user.messages[user.messages.length - 1].sender === user.id)) {
            scrollToBottom();
        }
        shouldScrollRef.current = true;
        if (activeUser.id) {
            sendNotification();
        }
        setSearchlist(
            sortByLatestMessage(user.dialogues.filter((dialogue) =>
                dialogue.username.toLowerCase().startsWith(search.toLowerCase())
            ))
        );
        // eslint-disable-next-line
    }, [user]);

    useEffect(() => {
        user.active = activeUser.id;
        delete_notification();
        scrollToBottom();
        // eslint-disable-next-line
    }, [activeUser]);

    const sendMessage = (message, receiver) => {
        socket.send(JSON.stringify({
            field: "message",
            message: message,
            receiver: receiver
        }));
    };

    const send = () => {
        if (sendingMessage.trim() === "") {
            setsendingMessage("");
            return;
        }
        sendMessage(sendingMessage, activeUser.id);
        setsendingMessage("");
    };

    function handleActiveUser(id) {
        showChatbox()
        let active = user.dialogues.find((user) => user.id === id);
        setUser(prevUser => ({
            ...prevUser,
            notifications: [...prevUser.notifications.filter(element => element !== activeUser.id)]
        }));
        setActiveUser(active);
    }

    function render_messages(message, index, unreadCount, totalMessages) {
        if (Object.keys(activeUser).length === 0) return;
        if (message.sender === activeUser.id || (message.sender === user.id && message.receiver === activeUser.id)) {
            const isMine = message.sender === user.id;
            const isVideo = message.text && message.text.startsWith(subdomain);
            const isImage = message.text && message.text.startsWith(imageDelivery);
            const isStar = message.text && message.text.startsWith("STAR_SEND:");
            const isWarning = message.text === "This user only accepts messages from subscribers. You cannot send a message.";

            // Determine if this is the start of the unread messages
            const isFirstUnread = !isMine && unreadCount > 0 && index === (totalMessages - unreadCount);

            return (
                <div key={message.id || `msg-${index}-${message.date}`}>
                    {isFirstUnread && (
                        <div className="flex items-center gap-4 my-6">
                            <div className="flex-1 h-px bg-red-200"></div>
                            <span className="text-xs font-bold text-red-500 uppercase tracking-wider px-3 py-1 bg-red-50 rounded-full border border-red-100">
                                {unreadCount} {t('chats.newMessage')}{unreadCount > 1 ? 's' : ''}
                            </span>
                            <div className="flex-1 h-px bg-red-200"></div>
                        </div>
                    )}
                    {isWarning ? (
                        <div className="flex justify-center mb-3">
                            <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl max-w-[85%] animate-fade-in">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                                <p className="text-xs font-medium text-amber-800">{t('chats.subOnlyWarning')}</p>
                            </div>
                        </div>
                    ) : (
                        <li className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-3`}>
                            <div className={`max-w-[75%] rounded-2xl text-sm leading-relaxed overflow-hidden ${isStar
                                ? `bg-gradient-to-br from-[#20c9b5] to-[#0f8a7e] text-white shadow-lg shadow-[#20c9b5]/20 ${isMine ? 'rounded-br-md' : 'rounded-bl-md'}`
                                : isMine
                                    ? 'bg-primary-600 text-white rounded-br-md'
                                    : 'bg-white text-slate-700 border border-slate-100 rounded-bl-md shadow-sm'
                                }`}>
                                {isVideo ? (
                                    <VideoMessage message={message} isMine={isMine} />
                                ) : isImage ? (
                                    <div className="w-64 sm:w-80">
                                        <img
                                            src={`${message.text}`}
                                            alt=""
                                            className="w-full cursor-pointer"
                                            onClick={() => window.open(message.text, '_blank')}
                                        />
                                        <div className="px-3 py-1.5">
                                            <span className={`text-[10px] block text-right ${isMine ? 'text-white/60' : 'text-slate-400'}`}>{isoDate(message.date)}</span>
                                        </div>
                                    </div>
                                ) : isStar ? (
                                    <div className="px-5 py-4 flex flex-col items-center justify-center min-w-[140px] text-center gap-2">
                                        <img src="/star192.png" alt="star" className="w-14 h-14 object-contain filter drop-shadow-md animate-bounce" style={{ animationDuration: '2s' }} />
                                        <div>
                                            <p className="font-black text-2xl mb-0 text-white drop-shadow-sm">
                                                +{message.text.split(":")[1]}
                                            </p>
                                            <p className="text-xs font-bold uppercase tracking-wide text-[#e6fcf9] opacity-90">
                                                {t('chats.starsSent')}
                                            </p>
                                        </div>
                                        <span className="text-[10px] block w-full text-right mt-1 text-white/70">{isoDate(message.date)}</span>
                                    </div>
                                ) : (
                                    <div className="px-4 py-2.5">
                                        <p className="mb-1">{message.text}</p>
                                        <span className={`text-[10px] block text-right ${isMine ? 'text-white/60' : 'text-slate-400'}`}>{isoDate(message.date)}</span>
                                    </div>
                                )}
                            </div>
                        </li>
                    )}
                </div>
            )
        }
    }

    const get_more_messages = () => {
        if (chatContainerRef.current) {
            scrollState.current = {
                isPrepending: true,
                height: chatContainerRef.current.scrollHeight,
                top: chatContainerRef.current.scrollTop
            };
        }
        socket.send(JSON.stringify({
            field: "more_messages",
            other_user: activeUser.id,
            message_count: user.messages.filter(msg => msg.sender === activeUser.id || msg.receiver === activeUser.id).length
        }));
        shouldScrollRef.current = false;
    };

    useLayoutEffect(() => {
        if (scrollState.current.isPrepending && chatContainerRef.current) {
            const newHeight = chatContainerRef.current.scrollHeight;
            const heightDiff = newHeight - scrollState.current.height;
            chatContainerRef.current.scrollTop = scrollState.current.top + heightDiff;
            scrollState.current.isPrepending = false;
        }
    }, [user.messages]);

    const sendNotification = () => {
        if (socket) {
            socket.send(JSON.stringify({
                field: "notificaiton",
                id: activeUser.id
            }));
        }
    };

    const remove_block = async () => {
        const res = await api.post('api/dialogue_spam/', { spam: false, id: activeUser.id });
        if (res.status === 200) {
            hideChatbox();
            setUser(prevUser => ({
                ...prevUser,
                dialogues: prevUser.dialogues.map(dialogue => {
                    if (dialogue.id === activeUser.id) return { ...dialogue, spam: false };
                    return dialogue;
                })
            }));
        }
    }

    const block = async () => {
        const res = await api.post('api/dialogue_spam/', { spam: true, id: activeUser.id });
        if (res.status === 200) {
            hideChatbox();
            setUser(prevUser => ({
                ...prevUser,
                dialogues: prevUser.dialogues.map(dialogue => {
                    if (dialogue.id === activeUser.id) return { ...dialogue, spam: true };
                    return dialogue;
                })
            }));
        }
    }

    const renderUserItem = (users) => {
        const notifCount = (() => {
            if (activeUser.id === users.id) return 0;
            return user.notifications.filter(element => element === users.id).length;
        })();

        // Find subscription info:
        // 1. From dialogue object directly (if backend includes it)
        // 2. From user.subs matching by username (subs.id is record ID, not user ID)
        const subInfo = (() => {
            // Priority 1: dialogue object has subscription field directly
            if (users.subscription) return users.subscription;
            // Priority 2: match from subs list by subscriber_name
            // (subs[i].username = creator's name, subscriber_name = the subscriber's name)
            const subs = user.subs || [];
            const found = subs.find(s => s.subscriber_name === users.username);
            return found?.subscription || null;
        })();

        const subBadge = (() => {
            if (!subInfo) return null;
            const isActive = subInfo.status === 'active';
            // Handle both field name variants: cancel_at_period_end (Stripe subs) and cancel_at (own sub)
            const isCanceling =
                subInfo.cancel_at_period_end === true ||
                (subInfo.cancel_at != null && subInfo.cancel_at !== '');

            if (!isActive) {
                return { label: 'Pasif', color: 'text-red-600 bg-red-50' };
            }
            if (isCanceling) {
                const rawDate =
                    subInfo.current_period_end ||
                    subInfo.cancel_at ||
                    subInfo.canceled_at ||
                    subInfo.ended_at;
                const date = rawDate
                    ? new Date(rawDate).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
                    : '—';
                return { label: `Cancels ${date}`, color: 'text-amber-600 bg-amber-50' };
            }
            return { label: 'Active', color: 'text-emerald-600 bg-emerald-50' };
        })();


        return (
            <button key={users.id} onClick={() => handleActiveUser(users.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition duration-200 text-left
                    ${activeUser.id === users.id
                        ? 'bg-primary-50 border border-primary-200'
                        : notifCount > 0
                            ? 'bg-white shadow-[0_2px_12px_rgba(244,63,94,0.15)] border-l-4 border-l-red-500 hover:bg-red-50/30 font-semibold'
                            : 'hover:bg-slate-50'
                    }`}
            >
                <img src={users.pp} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 min-w-0">
                        <h3 className={`text-sm truncate ${notifCount > 0 ? 'text-slate-900 font-bold' : 'text-slate-800 font-semibold'}`}>
                            {users.username}
                        </h3>
                        {subBadge && (
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${subBadge.color}`}>
                                {subBadge.label}
                            </span>
                        )}
                    </div>
                    {notifCount > 0 && (
                        <p className="text-xs text-red-600 font-bold animate-pulse">{notifCount} {t('chats.newNotif')}{notifCount > 1 ? 's' : ''}</p>
                    )}
                </div>
                {notifCount > 0 && (
                    <span className="w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center flex-shrink-0 shadow-sm animate-bounce">{notifCount}</span>
                )}
            </button>
        );
    };


    const requestUploadUrl = async () => {
        try {
            const res = await api.get('api/requesturlvideo/');
            if (res.status === 200) {
                const data = res.data;
                const urlEntry = data.find(([key]) => key === 'uploadURL' || key === 'upload_url');
                const uidEntry = data.find(([key]) => key === 'uid');
                if (urlEntry && uidEntry) {
                    setUploadUrl(urlEntry[1]);
                    setVideoUid(uidEntry[1]);
                    setVideoFile(null);
                }
            }
        } catch (error) {

        }
    };

    const sendVideo = async () => {
        if (!videoFile || !uploadUrl || !videoUid) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', videoFile);
            const res = await fetch(uploadUrl, {
                method: 'POST',
                body: formData,
            });

            if (!res.ok) {
                const errText = await res.text();
                throw new Error(`Upload failed with status ${res.status}: ${errText}`);
            }

            // Send video message as subdomain/uid
            const videoMessage = `${subdomain}/${videoUid}`;
            sendMessage(videoMessage, activeUser.id);
            setUploadUrl(null);
            setVideoUid(null);
            setVideoFile(null);
        } catch (error) {
            alert(`Video upload failed: ${error.message || 'Unknown error'}`);
        } finally {
            setUploading(false);
        }
    };

    const cancelVideoUpload = () => {
        setUploadUrl(null);
        setVideoUid(null);
        setVideoFile(null);
    };

    const requestPhotoUploadUrl = async () => {
        try {
            const res = await api.get('api/requesturlimage/');
            if (res.status === 200) {
                const data = res.data;
                setPhotoUploadUrl(data.upload_url);
                setPhotoImageId(data.image_id);
                setPhotoFile(null);
            }
        } catch (error) {
        }
    };

    const sendPhoto = async () => {
        if (!photoFile || !photoUploadUrl) return;
        setPhotoUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', photoFile);
            await fetch(photoUploadUrl, {
                method: 'POST',
                body: formData,
            });
            // Build delivery URL: imageDelivery/{imageId}/public
            const deliveryUrl = `${imageDelivery}/${photoImageId}/public`;
            sendMessage(deliveryUrl, activeUser.id);
            setPhotoUploadUrl(null);
            setPhotoImageId(null);
            setPhotoFile(null);
        } catch (error) {
        } finally {
            setPhotoUploading(false);
        }
    };

    const cancelPhotoUpload = () => {
        setPhotoUploadUrl(null);
        setPhotoImageId(null);
        setPhotoFile(null);
    };

    const handleSendStar = async (e) => {
        e.preventDefault();
        setStarError("");
        const amount = parseInt(starAmount, 10);
        if (isNaN(amount) || amount <= 0) return;

        try {
            const res = await api.post('api/receive-stars/', { amount, receiver_id: activeUser.id });
            if (res.status === 200) {
                user.stars -= amount;
                setUser({ ...user });
                sendMessage(`STAR_SEND:${amount}`, activeUser.id);
                setIsStarModalOpen(false);
                setStarAmount("");
            } else if (res.status === 400) {
                setStarError(t('chats.notEnoughStars'));
            }
        } catch (error) {
            if (error.response && error.response.status === 400) {
                setStarError(t('chats.notEnoughStars'));
            } else {
                setStarError(t('chats.starErrorMsg'));
            }
        }
    };


    return (
        <div className="bg-slate-50 flex flex-col overflow-hidden pb-[5.5rem]" style={{ height: '100dvh' }}>
            <div className="flex-1 flex overflow-hidden w-full">

                {/* Left sidebar — user list */}
                <div className={`w-full md:w-80 lg:w-96 flex-shrink-0 border-r border-slate-200 bg-white flex flex-col
                    ${isChatboxVisible ? 'hidden md:flex' : 'flex'}`}>

                    {/* Header + search */}
                    <div className="p-4 border-b border-slate-100 space-y-3">
                        <h2 className="text-lg font-bold text-slate-800">{t('chats.title')}</h2>
                        <input
                            type="text"
                            onChange={(e) => handleSearch(e.target.value)}
                            className="input"
                            placeholder={t('chats.searchPlaceholder')}
                        />
                        {/* Tabs */}
                        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
                            <button
                                onClick={() => setActiveTab("chats")}
                                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${activeTab === "chats" ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >{t('chats.tabChats')}</button>
                            <button
                                onClick={() => setActiveTab("archived")}
                                className={`relative flex-1 py-2 text-xs font-semibold rounded-lg transition ${activeTab === "archived" ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >
                                {t('chats.tabArchived')}
                                {archivedUsers.some(id => user.notifications.includes(id)) && activeTab !== "archived" && (
                                    <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                                )}
                            </button>
                            <button
                                onClick={() => setActiveTab("blocked")}
                                className={`flex-1 py-2 text-xs font-semibold rounded-lg transition ${activeTab === "blocked" ? 'bg-white text-primary-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                            >{t('chats.tabBlocked')}</button>
                        </div>
                    </div>

                    {/* User list */}
                    <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                        {searchlist.map((users) => {
                            if (activeTab === "chats" && users.spam === false && !isArchived(users.id)) return renderUserItem(users);
                            if (activeTab === "archived" && users.spam === false && isArchived(users.id)) return renderUserItem(users);
                            if (activeTab === "blocked" && users.spam === true) return renderUserItem(users);
                            return null;
                        })}
                    </div>
                </div>

                {/* Right side — chatbox */}
                <div className={`flex-1 flex flex-col bg-slate-50 overflow-hidden
                    ${isChatboxVisible ? 'flex' : 'hidden md:flex'}`}>

                    {Object.keys(activeUser).length === 0 ? (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="text-center text-slate-400">
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-16 h-16 mx-auto mb-4 text-slate-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                <p className="font-medium">{t('chats.selectConversation')}</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Chat header */}
                            <div className="flex items-center gap-3 px-4 py-3 bg-white border-b border-slate-200 flex-shrink-0">
                                {/* Back button (mobile) */}
                                <button onClick={hideChatbox} className="md:hidden text-slate-400 hover:text-slate-600 transition">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                    </svg>
                                </button>

                                <img src={activeUser.pp} alt="" className="w-10 h-10 rounded-full object-cover ring-2 ring-slate-100" />
                                <div className="flex-1 min-w-0">
                                    <a href={activeUser.username} className="text-sm font-semibold text-slate-800 hover:text-primary-600 transition no-underline">{activeUser.username}</a>
                                </div>

                                {user.dialogues.find((d) => d.id === activeUser.id)?.spam === true ? (
                                    <button onClick={remove_block} className="text-xs font-semibold text-emerald-600 hover:text-emerald-700 transition px-3 py-1.5 bg-emerald-50 rounded-lg">{t('chats.unblockBtn')}</button>
                                ) : (
                                    <div className="flex items-center gap-1.5">
                                        {isArchived(activeUser.id) ? (
                                            <button onClick={() => unarchiveUser(activeUser.id)} className="text-xs font-semibold text-amber-600 hover:text-amber-700 transition px-3 py-1.5 bg-amber-50 rounded-lg">{t('chats.unarchiveBtn')}</button>
                                        ) : (
                                            <button onClick={() => archiveUser(activeUser.id)} className="text-xs font-semibold text-slate-500 hover:text-slate-700 transition px-3 py-1.5 bg-slate-100 rounded-lg">{t('chats.archiveBtn')}</button>
                                        )}
                                        <button onClick={block} className="text-xs font-semibold text-red-600 hover:text-red-700 transition px-3 py-1.5 bg-red-50 rounded-lg">{t('chats.blockBtn')}</button>
                                    </div>
                                )}
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto px-4 py-4 custom-scrollbar min-h-0" ref={chatContainerRef}>
                                <ul className="list-none p-0 m-0">
                                    {is_include() && (
                                        <div className="text-center mb-4">
                                            <button onClick={get_more_messages} className="text-xs font-medium text-primary-600 hover:text-primary-700 transition">
                                                {t('chats.showPrevious')}
                                            </button>
                                        </div>
                                    )}
                                    {user.messages.map((message, index) => {
                                        const unreadCount = user.notifications.filter(n => n === activeUser.id).length;
                                        return render_messages(message, index, unreadCount, user.messages.length);
                                    })}
                                    {uploading && (
                                        <li className="flex justify-end mb-3">
                                            <div className="max-w-[75%] rounded-2xl text-sm leading-relaxed overflow-hidden bg-primary-600 text-white rounded-br-md">
                                                <div className="w-64 sm:w-80">
                                                    <div className="relative bg-slate-900" style={{ paddingTop: '56.25%' }}>
                                                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-slate-900">
                                                            <div className="flex flex-col items-center gap-2">
                                                                <div className="spinner-sm border-white/20 border-t-white"></div>
                                                                <span className="text-[10px] text-white/70">{t('chats.uploadingVid')}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="px-3 py-1.5">
                                                        <span className="text-[10px] block text-right text-white/60">{t('chats.sending')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </li>
                                    )}
                                    <div ref={messageEndRef} />
                                </ul>
                            </div>

                            {/* Video upload panel */}
                            {uploadUrl && (
                                <div className="px-3 sm:px-4 py-3 bg-primary-50 border-t border-primary-200 flex-shrink-0 relative">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            <input
                                                ref={videoInputRef}
                                                id="video-file-input"
                                                type="file"
                                                accept="video/*"
                                                disabled={uploading}
                                                onChange={(e) => setVideoFile(e.target.files[0] || null)}
                                                className="hidden"
                                            />
                                            <label
                                                htmlFor="video-file-input"
                                                className={`text-xs sm:text-sm font-medium px-3 py-1.5 rounded-lg cursor-pointer transition ${uploading ? 'opacity-50 cursor-not-allowed bg-primary-100 text-primary-700' : 'bg-primary-100 text-primary-700 hover:bg-primary-200'}`}
                                            >
                                                {t('chats.chooseFile')}
                                            </label>
                                            <span className="text-xs text-slate-500 truncate flex-1 min-w-0">
                                                {videoFile ? videoFile.name : t('chats.noFileChosen')}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 justify-end flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={sendVideo}
                                                disabled={!videoFile || uploading}
                                                className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {uploading ? (
                                                    <div className="spinner-sm"></div>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                    </svg>
                                                )}
                                                {t('chats.sendBtn')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={cancelVideoUpload}
                                                className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition flex-shrink-0"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Photo upload panel */}
                            {photoUploadUrl && (
                                <div className="px-3 sm:px-4 py-3 bg-emerald-50 border-t border-emerald-200 flex-shrink-0">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-emerald-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <input
                                                ref={photoInputRef}
                                                id="photo-file-input"
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setPhotoFile(e.target.files[0] || null)}
                                                className="hidden"
                                            />
                                            <label
                                                htmlFor="photo-file-input"
                                                className="text-xs sm:text-sm font-medium px-3 py-1.5 rounded-lg cursor-pointer bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition"
                                            >
                                                {t('chats.chooseFile')}
                                            </label>
                                            <span className="text-xs text-slate-500 truncate flex-1 min-w-0">
                                                {photoFile ? photoFile.name : t('chats.noFileChosen')}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 justify-end flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={sendPhoto}
                                                disabled={!photoFile || photoUploading}
                                                className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {photoUploading ? (
                                                    <div className="spinner-sm"></div>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                    </svg>
                                                )}
                                                {t('chats.sendBtn')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={cancelPhotoUpload}
                                                className="w-8 h-8 flex items-center justify-center rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition flex-shrink-0"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}


                            {/* Send box */}
                            {activeUser.spam !== true && (
                                <div className="px-4 py-3 bg-white border-t border-slate-200 flex-shrink-0">
                                    <div className="flex items-center gap-2">
                                        {/* Collapsible attachment menu */}
                                        <div className="relative flex-shrink-0">
                                            <button
                                                type="button"
                                                onClick={() => setIsAttachMenuOpen(!isAttachMenuOpen)}
                                                className={`w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 ${isAttachMenuOpen
                                                    ? 'bg-primary-600 text-white rotate-90'
                                                    : 'text-slate-400 hover:text-primary-600 hover:bg-primary-50'
                                                    }`}
                                                title="Attachments"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                                    <circle cx="12" cy="5" r="2" />
                                                    <circle cx="12" cy="12" r="2" />
                                                    <circle cx="12" cy="19" r="2" />
                                                </svg>
                                            </button>

                                            {/* Popover menu (appears upward) */}
                                            {isAttachMenuOpen && (
                                                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-200">
                                                    {/* Video upload button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => { requestUploadUrl(); setIsAttachMenuOpen(false); }}
                                                        className="w-11 h-11 flex items-center justify-center rounded-full bg-white shadow-lg border border-slate-200 text-slate-500 hover:text-primary-600 hover:border-primary-300 hover:shadow-primary-100 transition-all duration-200"
                                                        title="Video"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                        </svg>
                                                    </button>
                                                    {/* Photo upload button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => { requestPhotoUploadUrl(); setIsAttachMenuOpen(false); }}
                                                        className="w-11 h-11 flex items-center justify-center rounded-full bg-white shadow-lg border border-slate-200 text-slate-500 hover:text-emerald-600 hover:border-emerald-300 hover:shadow-emerald-100 transition-all duration-200"
                                                        title="Photo"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </button>
                                                    {/* Star send button */}
                                                    <button
                                                        type="button"
                                                        onClick={() => { setIsStarModalOpen(true); setStarError(""); setStarAmount(""); setIsAttachMenuOpen(false); }}
                                                        className="w-11 h-11 flex items-center justify-center rounded-full bg-gradient-to-br from-[#e6fcf9] to-[#b2f0e8] shadow-lg border border-[#20c9b5]/30 hover:from-[#20c9b5] hover:to-[#17a898] hover:shadow-md group transition-all duration-200"
                                                        title="Send Stars"
                                                    >
                                                        <img src="/star192.png" alt="Star icon" className="w-6 h-6 object-contain group-hover:drop-shadow-sm group-hover:scale-110 transition-transform duration-200" />
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <input
                                            required type="text"
                                            onChange={(e) => setsendingMessage(e.target.value)}
                                            value={sendingMessage}
                                            className="input flex-1"
                                            placeholder={t('chats.writeMessage')}
                                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
                                        />
                                        <button
                                            onClick={send}
                                            type="button"
                                            className="w-10 h-10 flex items-center justify-center rounded-full text-primary-600 hover:bg-primary-50 active:bg-primary-100 transition-all duration-200 flex-shrink-0"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Star Send Modal */}
            {isStarModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => { setIsStarModalOpen(false); setStarError(""); }}></div>
                    <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                        {/* Decorative header */}
                        <div className="h-24 bg-gradient-to-br from-[#20c9b5] via-[#a0ddd5] to-[#0f8a7e] flex items-center justify-center">
                            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-lg -mb-12 border-4 border-white">
                                <img src="/star192.png" alt="Star" className="w-10 h-10 object-contain drop-shadow-sm" />
                            </div>
                        </div>

                        <div className="px-6 pt-12 pb-6 text-center">
                            <h3 className="text-xl font-black text-slate-800 mb-1">{t('chats.sendStars')}</h3>
                            <p className="text-sm text-slate-500 mb-6">{t('chats.giftStars')} <span className="font-semibold text-slate-700">{activeUser.username}</span></p>

                            <form onSubmit={handleSendStar}>
                                {starError && (
                                    <div className="mb-4 bg-red-50 text-red-600 text-sm font-semibold py-3 px-4 rounded-xl border border-red-200 shadow-sm animate-in fade-in slide-in-from-top-2">
                                        {starError}
                                    </div>
                                )}
                                <div className="relative mb-6">
                                    <input
                                        type="number"
                                        min="1"
                                        step="1"
                                        required
                                        value={starAmount}
                                        onChange={(e) => setStarAmount(e.target.value)}
                                        className="w-full text-center text-3xl font-black text-slate-800 border-2 border-slate-200 rounded-2xl py-4 focus:border-[#20c9b5] focus:ring-4 focus:ring-[#20c9b5]/20 outline-none transition-all placeholder:text-slate-300"
                                        placeholder="0"
                                        autoFocus
                                    />
                                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                                        <img src="/star192.png" alt="star" className="w-8 h-8 opacity-40 grayscale" />
                                    </div>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        type="button"
                                        onClick={() => { setIsStarModalOpen(false); setStarError(""); }}
                                        className="flex-1 py-3 px-4 rounded-xl font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                                    >
                                        {t('chats.cancelBtn')}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={!starAmount || parseInt(starAmount, 10) <= 0}
                                        className="flex-1 py-3 px-4 rounded-xl font-bold text-white bg-gradient-to-r from-[#20c9b5] to-[#17a898] hover:shadow-lg hover:shadow-[#20c9b5]/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform active:scale-95 flex items-center justify-center gap-2"
                                    >
                                        {t('chats.sendBtn')}
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                        </svg>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
export default Chats;